import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { SearchableSelect } from '../../components/ui/searchable-select';
import { useToast } from '../../components/ui/use-toast';
import { Plus, Pencil, Trash2, Search, ChevronLeft, History, PackagePlus } from 'lucide-react';
import { useRestaurant } from '../../context/RestaurantContext';
import { confirm } from '../../components/ui/confirm-dialog';
import { listIngredients, updateIngredient } from '../../services/ingredientService';
import { locationsService } from '../../services/locationsService';
import {
  listPurchases, createPurchase, updatePurchase, deletePurchase,
  getInventorySummary, getItemHistory,
} from '../../services/inventoryService';

const todayISO = () => new Date().toISOString().slice(0, 10);
const EMPTY_PURCHASE = () => ({
  ingredient_id: '', purchase_date: todayISO(), quantity: '',
  unit: '', unit_cost: '', supplier: '', notes: '',
});
const fmtQty = (n) => {
  const v = Number(n || 0);
  return Number.isInteger(v) ? String(v) : v.toFixed(3).replace(/\.?0+$/, '');
};

export default function InventoryManagement() {
  const { restaurantId: urlRestaurantId, locationId } = useParams();
  const { restaurantId: ctxRestaurantId } = useRestaurant();
  const restaurantId = urlRestaurantId || ctxRestaurantId;
  const navigate = useNavigate();
  const { toast } = useToast();

  const [locationName, setLocationName] = useState('');
  const [ingredients, setIngredients] = useState([]);
  // Full ingredient list (tracked + untracked) — drives the "Track items" picker.
  const [allIngredients, setAllIngredients] = useState([]);
  const [summary, setSummary] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Item-tracking picker. null = closed; otherwise { selected: number[], q: '' }.
  const [trackPicker, setTrackPicker] = useState(null);
  const [trackSaving, setTrackSaving] = useState(false);

  // Record/edit-purchase dialog. null = closed.
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  // Per-item drill-down dialog.
  const [drill, setDrill] = useState(null); // { ingredient, purchases, consumption } | null
  const [drillLoading, setDrillLoading] = useState(false);

  const ingredientName = useCallback(
    (id) => ingredients.find((i) => i.id === id)?.name || `#${id}`,
    [ingredients],
  );

  const load = useCallback(async () => {
    if (!restaurantId || !locationId) return;
    setLoading(true);
    try {
      const [ings, sum, purch] = await Promise.all([
        listIngredients(restaurantId),
        getInventorySummary(restaurantId, locationId),
        listPurchases(restaurantId, locationId),
      ]);
      // Only items the user opted into tracking can be purchased / shown here.
      setAllIngredients(ings);
      setIngredients(ings.filter((i) => i.track_inventory));
      setSummary(sum);
      setPurchases(purch);
    } catch (e) {
      toast({ title: 'Error', description: e.error || 'Failed to load inventory', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [restaurantId, locationId, toast]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let alive = true;
    if (!restaurantId || !locationId) return undefined;
    locationsService.list(restaurantId)
      .then((rows) => {
        if (!alive) return;
        const loc = (Array.isArray(rows) ? rows : []).find((l) => String(l.id) === String(locationId));
        setLocationName(loc?.name || '');
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [restaurantId, locationId]);

  const filteredSummary = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return summary;
    return summary.filter((r) => r.name.toLowerCase().includes(q));
  }, [summary, search]);

  const ingredientOptions = useMemo(
    () => ingredients.map((i) => ({ value: String(i.id), label: i.name })),
    [ingredients],
  );

  // The item-tracking picker offers active ingredients not yet opted in, the same tick as Settings.
  const untrackedIngredients = useMemo(
    () => allIngredients.filter((i) => !i.track_inventory && i.is_active),
    [allIngredients],
  );

  const pickerList = useMemo(() => {
    if (!trackPicker) return [];
    const q = trackPicker.q.trim().toLowerCase();
    return q
      ? untrackedIngredients.filter((i) => i.name.toLowerCase().includes(q))
      : untrackedIngredients;
  }, [trackPicker, untrackedIngredients]);

  const openTrackPicker = () => setTrackPicker({ selected: [], q: '' });

  const toggleTrackPick = (id) => setTrackPicker((p) => {
    const has = p.selected.includes(id);
    return { ...p, selected: has ? p.selected.filter((x) => x !== id) : [...p.selected, id] };
  });

  const handleTrackSave = async () => {
    if (!trackPicker.selected.length) { setTrackPicker(null); return; }
    setTrackSaving(true);
    try {
      await Promise.all(trackPicker.selected.map((id) => {
        const ing = allIngredients.find((i) => i.id === id);
        return updateIngredient(restaurantId, id, {
          name: ing.name,
          description: ing.description || '',
          base_unit: ing.base_unit || '',
          track_inventory: true,
          is_active: ing.is_active,
        });
      }));
      toast({
        title: 'Tracking on',
        description: `${trackPicker.selected.length} item(s) added to inventory tracking.`,
      });
      setTrackPicker(null);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.error || 'Failed to update items', variant: 'destructive' });
    } finally {
      setTrackSaving(false);
    }
  };

  // ---- purchase save / delete ----------------------------------------------

  const openCreate = () => setEditing(EMPTY_PURCHASE());
  const openEdit = (p) => setEditing({
    id: p.id,
    ingredient_id: String(p.ingredient_id),
    purchase_date: p.purchase_date || todayISO(),
    quantity: String(p.quantity ?? ''),
    unit: p.unit || '',
    unit_cost: p.unit_cost != null ? String(p.unit_cost) : '',
    supplier: p.supplier || '',
    notes: p.notes || '',
  });

  const handleSave = async () => {
    if (!editing.ingredient_id) {
      toast({ title: 'Item required', description: 'Pick an item to record a purchase.', variant: 'destructive' });
      return;
    }
    if (!(Number(editing.quantity) > 0)) {
      toast({ title: 'Quantity required', description: 'Enter a quantity greater than 0.', variant: 'destructive' });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ingredient_id: Number(editing.ingredient_id),
        purchase_date: editing.purchase_date,
        quantity: Number(editing.quantity),
        unit: editing.unit || '',
        unit_cost: editing.unit_cost === '' ? '' : Number(editing.unit_cost),
        supplier: editing.supplier || '',
        notes: editing.notes || '',
      };
      if (editing.id) {
        await updatePurchase(restaurantId, locationId, editing.id, payload);
      } else {
        await createPurchase(restaurantId, locationId, payload);
      }
      toast({ title: 'Saved', description: 'Purchase recorded.' });
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.error || 'Failed to save purchase', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!(await confirm({
      description: `Delete this purchase of ${ingredientName(p.ingredient_id)}?`,
      confirmText: 'Delete', tone: 'destructive',
    }))) return;
    try {
      await deletePurchase(restaurantId, locationId, p.id);
      toast({ title: 'Deleted' });
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.error || 'Failed to delete', variant: 'destructive' });
    }
  };

  // When the picked item has a base unit, prefill the purchase unit.
  const onPickIngredient = (val) => {
    const ing = ingredients.find((i) => String(i.id) === String(val));
    setEditing((prev) => ({
      ...prev,
      ingredient_id: val,
      unit: prev.unit || ing?.base_unit || '',
    }));
  };

  // ---- drill-down ----------------------------------------------------------

  const openDrill = async (row) => {
    setDrillLoading(true);
    setDrill({ ingredient: { id: row.ingredient_id, name: row.name, base_unit: row.base_unit }, purchases: [], consumption: [] });
    try {
      const data = await getItemHistory(restaurantId, locationId, row.ingredient_id);
      setDrill({
        ingredient: data.ingredient || { id: row.ingredient_id, name: row.name, base_unit: row.base_unit },
        purchases: data.purchases || [],
        consumption: data.consumption || [],
      });
    } catch (e) {
      toast({ title: 'Error', description: e.error || 'Failed to load history', variant: 'destructive' });
      setDrill(null);
    } finally {
      setDrillLoading(false);
    }
  };

  if (!restaurantId || !locationId) {
    return <div className="p-6 text-muted-foreground">No location selected.</div>;
  }

  return (
    <div className="w-full p-6 space-y-6">
      <div>
        <button
          type="button"
          onClick={() => navigate(`/restaurant/${restaurantId}/inventory`)}
          className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-muted-foreground font-mono mb-1 hover:text-foreground"
        >
          <ChevronLeft className="h-3 w-3" /> Inventory · {locationName || 'Location'}
        </button>
        <h1 className="font-display text-3xl font-semibold text-foreground tracking-tight leading-none">Item Tracking</h1>
      </div>

      <Tabs defaultValue="items">
        <TabsList>
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="purchases">Purchases</TabsTrigger>
        </TabsList>

        {/* ---------- ITEMS (summary) ---------- */}
        <TabsContent value="items">
          <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-3">
              <div>
                <CardTitle>Stock overview</CardTitle>
              </div>
              <Button variant="outline" onClick={openTrackPicker} className="shrink-0">
                <PackagePlus className="h-4 w-4 mr-2" /> Track items
              </Button>
            </CardHeader>
            <CardContent>
              <div className="mb-4 relative max-w-sm">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search items..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Total Purchased</TableHead>
                      <TableHead className="text-right">Total Consumed</TableHead>
                      <TableHead className="text-right">Orders</TableHead>
                      <TableHead>Last Purchase</TableHead>
                      <TableHead className="text-right">History</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading && (
                      <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">Loading…</TableCell></TableRow>
                    )}
                    {!loading && filteredSummary.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">
                        No tracked items yet.{' '}
                        <button type="button" className="underline hover:text-foreground" onClick={openTrackPicker}>Track items</button>{' '}
                        to pick which ingredients to follow here.
                      </TableCell></TableRow>
                    )}
                    {filteredSummary.map((r) => (
                      <TableRow
                        key={r.ingredient_id}
                        className="cursor-pointer"
                        onClick={() => openDrill(r)}
                      >
                        <TableCell className="font-medium">
                          {r.name}
                          {!r.is_active && <span className="ml-2 text-xs text-slate-400">(inactive)</span>}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(r.total_purchased)}{r.base_unit ? ` ${r.base_unit}` : ''}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {fmtQty(r.total_consumed)}{r.base_unit ? ` ${r.base_unit}` : ''}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{r.order_count}</TableCell>
                        <TableCell className="text-sm text-slate-600">{r.last_purchase_date || '—'}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openDrill(r); }}>
                            <History className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ---------- PURCHASES (ledger) ---------- */}
        <TabsContent value="purchases">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Purchase ledger</CardTitle>
              </div>
              <Button onClick={openCreate} disabled={ingredients.length === 0}>
                <Plus className="h-4 w-4 mr-2" /> Record Purchase
              </Button>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead className="text-right">Unit Cost</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading && (
                      <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">Loading…</TableCell></TableRow>
                    )}
                    {!loading && purchases.length === 0 && (
                      <TableRow><TableCell colSpan={6} className="text-center py-6 text-slate-500">No purchases recorded yet.</TableCell></TableRow>
                    )}
                    {purchases.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="text-sm">{p.purchase_date}</TableCell>
                        <TableCell className="font-medium">{p.ingredient_name || ingredientName(p.ingredient_id)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtQty(p.quantity)}{p.unit ? ` ${p.unit}` : ''}</TableCell>
                        <TableCell className="text-right tabular-nums">{p.unit_cost != null ? p.unit_cost : '—'}</TableCell>
                        <TableCell className="text-sm text-slate-600">{p.supplier || '—'}</TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(p)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(p)} className="text-red-600 hover:text-red-700">
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ---------- Track items picker ---------- */}
      <Dialog open={trackPicker !== null} onOpenChange={(o) => !o && setTrackPicker(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Track items</DialogTitle>
          </DialogHeader>
          {trackPicker && (
            <div className="space-y-3">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Search ingredients…"
                  value={trackPicker.q}
                  onChange={(e) => setTrackPicker({ ...trackPicker, q: e.target.value })}
                  className="pl-9"
                />
              </div>
              <div className="border rounded-lg divide-y max-h-72 overflow-y-auto">
                {untrackedIngredients.length === 0 && (
                  <div className="px-3 py-6 text-center text-sm text-slate-500">
                    Every active ingredient is already tracked. Create more under{' '}
                    <button type="button" className="underline hover:text-foreground" onClick={() => navigate('/settings/ingredients')}>Settings → Ingredients</button>.
                  </div>
                )}
                {untrackedIngredients.length > 0 && pickerList.length === 0 && (
                  <div className="px-3 py-6 text-center text-sm text-slate-500">No ingredients match your search.</div>
                )}
                {pickerList.map((ing) => {
                  const checked = trackPicker.selected.includes(ing.id);
                  return (
                    <label
                      key={ing.id}
                      className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-50"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleTrackPick(ing.id)}
                        className="h-4 w-4"
                      />
                      <span className="flex-1 text-sm font-medium">{ing.name}</span>
                      {ing.base_unit && <span className="text-xs text-slate-400">{ing.base_unit}</span>}
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTrackPicker(null)}>Cancel</Button>
            <Button
              onClick={handleTrackSave}
              disabled={trackSaving || !trackPicker?.selected.length}
            >
              {trackSaving
                ? 'Saving…'
                : `Track ${trackPicker?.selected.length || ''} item${trackPicker?.selected.length === 1 ? '' : 's'}`.trim()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Record / edit purchase dialog ---------- */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit Purchase' : 'Record Purchase'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Item *</Label>
                <SearchableSelect
                  options={ingredientOptions}
                  value={editing.ingredient_id}
                  onChange={onPickIngredient}
                  placeholder="Select an item…"
                  searchPlaceholder="Search items…"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={editing.purchase_date}
                    onChange={(e) => setEditing({ ...editing, purchase_date: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Quantity *</Label>
                  <Input
                    type="number" min="0" step="any"
                    value={editing.quantity}
                    onChange={(e) => setEditing({ ...editing, quantity: e.target.value })}
                    placeholder="e.g., 5"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Input
                    value={editing.unit}
                    onChange={(e) => setEditing({ ...editing, unit: e.target.value })}
                    placeholder="kg, litre, units…"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Unit Cost</Label>
                  <Input
                    type="number" min="0" step="any"
                    value={editing.unit_cost}
                    onChange={(e) => setEditing({ ...editing, unit_cost: e.target.value })}
                    placeholder="optional"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Supplier</Label>
                <Input
                  value={editing.supplier}
                  onChange={(e) => setEditing({ ...editing, supplier: e.target.value })}
                  placeholder="optional"
                />
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  rows={2}
                  value={editing.notes}
                  onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                  placeholder="Optional notes"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------- Per-item history drill-down ---------- */}
      <Dialog open={drill !== null} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{drill?.ingredient?.name || 'Item'} — history</DialogTitle>
          </DialogHeader>
          {drillLoading ? (
            <div className="py-8 text-center text-slate-500">Loading…</div>
          ) : drill && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-semibold mb-2">Purchases</h4>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader className="bg-slate-50">
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {drill.purchases.length === 0 && (
                        <TableRow><TableCell colSpan={2} className="text-center py-4 text-slate-400 text-sm">No purchases</TableCell></TableRow>
                      )}
                      {drill.purchases.map((row, i) => (
                        <TableRow key={`p-${i}`}>
                          <TableCell className="text-sm">{row.date}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {fmtQty(row.quantity)}{drill.ingredient?.base_unit ? ` ${drill.ingredient.base_unit}` : ''}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-semibold mb-2">Consumption</h4>
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader className="bg-slate-50">
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Orders</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {drill.consumption.length === 0 && (
                        <TableRow><TableCell colSpan={3} className="text-center py-4 text-slate-400 text-sm">No consumption yet</TableCell></TableRow>
                      )}
                      {drill.consumption.map((row, i) => (
                        <TableRow key={`c-${i}`}>
                          <TableCell className="text-sm">{row.date}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {fmtQty(row.quantity)}{drill.ingredient?.base_unit ? ` ${drill.ingredient.base_unit}` : ''}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{row.order_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDrill(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
